import { DashboardChartSkeleton } from '@shigo-ui/dashboard-ui';
import { Card, CardContent, CardHeader, CardTitle } from '@shigo-ui/ui';
import { ResponsiveLineChart, type LineChartDatum } from '@shigo-ui/viz';

import { dashboardT } from '../../../lib/messages';
import {
  dashboardMetricValue,
  dashboardTimeline,
  type DashboardOverview,
  DashboardMetric,
  type DashboardWidgetAction,
} from '../model';
import { dashboardMetricLabelKey, formatDashboardChartValue } from '../format';
import {
  buildDashboardChartAxisLayout,
  dashboardMetricUnit,
  type DashboardChartMetricAxisInput,
} from '../dashboardChartAxes';
import type { ReportExplorerRouteSearch } from '../../reporting/reportExplorer/reportExplorerRouteSearch';
import type { PerformanceAnalyzerRouteSearch } from '../../reporting/performanceAnalyzer/performanceAnalyzerRouteSearch';
import { DashboardWidgetActions } from './DashboardWidgetActions';

export type DashboardPerformanceSeries = Omit<DashboardChartMetricAxisInput, 'values' | 'color'> & {
  data: LineChartDatum[];
};

interface DashboardPerformanceChartProps {
  overview?: Pick<DashboardOverview, 'hours' | 'totalsByDay'> | undefined;
  metrics?: DashboardMetric[];
  metricSeries?: readonly DashboardPerformanceSeries[];
  title?: string;
  ariaLabel?: string;
  defaultVisibleMetrics?: readonly DashboardMetric[] | undefined;
  range: { dateFrom: string; dateTo: string };
  loading: boolean;
  error: boolean;
  actions: readonly DashboardWidgetAction[];
  reportingSearch?: ReportExplorerRouteSearch | null | undefined;
  performanceAnalyzerSearch?: PerformanceAnalyzerRouteSearch | null | undefined;
}

const colors = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
  'var(--chart-7)',
  'var(--chart-8)',
];

export function DashboardPerformanceChart({
  overview,
  metrics = [],
  metricSeries: suppliedSeries,
  title = dashboardT('dashboard.performance.title'),
  ariaLabel = dashboardT('dashboard.performance.ariaLabel'),
  defaultVisibleMetrics,
  range,
  loading,
  error,
  actions,
  reportingSearch,
  performanceAnalyzerSearch,
}: DashboardPerformanceChartProps) {
  const current = overview ? dashboardTimeline(overview, range) : [];
  const metricSeries =
    suppliedSeries ??
    metrics.map((metric): DashboardPerformanceSeries => ({
      metric,
      label: dashboardT(dashboardMetricLabelKey(metric)),
      unit: dashboardMetricUnit(metric),
      valueFormatter: (value) => formatDashboardChartValue(value, metric),
      data: current.flatMap((point, index) => {
        const value = chartValue(dashboardMetricValue(point, metric), metric);
        return value === undefined ? [] : [{ x: index, label: pointLabel(point), value }];
      }),
    }));
  const axisLayout = buildDashboardChartAxisLayout(
    metricSeries.map((item, index) => ({
      ...item,
      color: colors[index % colors.length] ?? 'var(--foreground)',
      values: item.data.map(({ value }) => value),
    })),
  );
  const series = metricSeries.map((item, index) => ({
    id: item.metric,
    label: item.label,
    color: colors[index % colors.length] ?? 'var(--foreground)',
    data: item.data,
    axisId: axisLayout.axisIdByMetric.get(item.metric),
  }));
  const defaultVisibleMetricSet = new Set(defaultVisibleMetrics);

  return (
    <Card className="border-border bg-card min-w-0 rounded-md shadow-[var(--shadow-xs)]">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 px-5 pt-5 pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        <DashboardWidgetActions
          actions={actions}
          reportingSearch={reportingSearch}
          performanceAnalyzerSearch={performanceAnalyzerSearch}
        />
      </CardHeader>
      <CardContent className="px-5 pt-0 pb-5">
        {loading ? <DashboardChartSkeleton label={dashboardT('dashboard.widget.loading')} /> : null}
        {!loading && error ? (
          <div className="text-destructive flex h-[340px] items-center justify-center text-sm">
            {dashboardT('dashboard.widget.error')}
          </div>
        ) : null}
        {!loading && !error ? (
          <ResponsiveLineChart
            ariaLabel={ariaLabel}
            axes={axisLayout.axes}
            series={series}
            defaultHiddenSeriesIds={
              defaultVisibleMetrics
                ? metrics.filter((metric) => !defaultVisibleMetricSet.has(metric))
                : undefined
            }
            emptyMessage={dashboardT('dashboard.widget.empty')}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

function chartValue(value: number | undefined, metric: DashboardMetric) {
  if (value === undefined) return undefined;
  return metric === DashboardMetric.FillRate ? value * 100 : value;
}

function pointLabel(point: { day: string; hour?: string | number | null | undefined }) {
  return point.hour === null || point.hour === undefined ? point.day : `${String(point.hour)}:00`;
}
